import React, { useState, useCallback, useContext } from 'react';
import { DataGridWrapper } from '../../../../Utils/DataGridWrapper/DataGridWrapper';
import { GridReactContext } from '../../../../Utils/DataGridWrapper/GridContext';
import { i18n } from '../../../../Utils/i18n/i18n';
import { getPendingApprovalsAPI, approveOrRejectUserUploadAPI } from '../../../../Services/api';
import { IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import { get, isEmpty } from 'lodash';
import { FilePreview, isPreviewableFile } from '../../../Smart/FilePreview/FilePreview';
import { ApproveUploadModal } from '../ApproveUploadModal/ApproveUploadModal';
import { RejectUploadModal } from '../RejectUploadModal/RejectUploadModal';
import { SignDocTypes } from '../../../SignDoc/SignDocLib';
import { SignDocApprovalActions } from '../../../SignDoc/SignDocApprovalActions/SignDocApprovalActions';
import { PhoneCell } from '../../../../Utils/PhoneCell/PhoneCell';
import './PendingApprovalsTable.scss';

const tableFilters = {
    showFilters: {
        search: true,
    },
    filterData: {},
    filterValues: {}
};

const getColumns = ({ handleApprove, handleRejectClick, handleViewFile }) => {
    return [
        {
            field: 'userName',
            headerName: i18n.translate('pendingApprovals.columns.userName'),
            flex: 1,
            minWidth: 150,
            valueGetter: (params) => {
                const user = get(params, 'row.user');
                return user ? `${user.firstName} ${user.lastName}` : '';
            }
        },
        {
            field: 'phone',
            headerName: i18n.translate('pendingApprovals.columns.phone'),
            flex: 1,
            minWidth: 150,
            valueGetter: (params) => {
                return get(params, 'row.user.phoneFormatted', '') || get(params, 'row.user.phone', '');
            },
            renderCell: (params) => <PhoneCell value={params.value} showTitle={false} />
        },
        {
            field: 'idNumber',
            headerName: i18n.translate('pendingApprovals.columns.idNumber'),
            flex: 1,
            minWidth: 150,
            valueGetter: (params) => {
                return get(params, 'row.user.idNumber', '');
            }
        },
        {
            field: 'signDocName',
            headerName: i18n.translate('pendingApprovals.columns.signDocName'),
            flex: 1,
            minWidth: 200,
            valueGetter: (params) => {
                return get(params, 'row.signDoc.docName', '');
            }
        },
        {
            field: 'uploadDate',
            headerName: i18n.translate('pendingApprovals.columns.uploadDate'),
            width: 150,
            valueGetter: (params) => {
                const files = get(params, 'row.additionalData.files', []);
                if (files.length > 0 && files[0].uploadedAt) {
                    return format(new Date(files[0].uploadedAt), 'dd/MM/yyyy HH:mm');
                }
                return '';
            }
        },
        {
            field: 'files',
            headerName: i18n.translate('pendingApprovals.columns.files'),
            width: 150,
            renderCell: (params) => {
                const files = get(params, 'row.additionalData.files', []);
                return (
                    <div className="files-cell">
                        {files.map((file, index) => (
                            <IconButton
                                key={index}
                                size="small"
                                onClick={() => handleViewFile(file)}
                                title={file.fileName}
                            >
                                <VisibilityIcon />
                            </IconButton>
                        ))}
                        {files.length === 0 && <span>-</span>}
                    </div>
                );
            }
        },
        {
            field: 'actions',
            headerName: i18n.translate('pendingApprovals.columns.actions'),
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <SignDocApprovalActions
                        row={params.row}
                        signDocType={SignDocTypes.UPLOAD_DOCUMENT}
                        isCurrentUserApprover={true} // In this table, user is always an approver
                        onApprove={handleApprove}
                        onReject={handleRejectClick}
                    />
                );
            }
        }
    ];
};

const convertData = (data) => {
    // Data is already in the correct format from apiToGetData transformation
    return Array.isArray(data) ? data : [];
};

export const PendingApprovalsTable = ({ organizationsOptions }) => {
    const gridContext = useContext(GridReactContext);
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [selectedUserSignDoc, setSelectedUserSignDoc] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [localTableFilters] = useState(() => {
        return {
            ...tableFilters,
            filterData: {
                ...tableFilters.filterData,
                organizations: organizationsOptions
            },
            filterValues: {
                ...tableFilters.filterValues,
                organizationIds: [get(organizationsOptions, '[0].value', 'all')]
            }
        };
    });

    const handleApprove = useCallback(async (userSignDocId, expiryDate) => {
        try {
            await approveOrRejectUserUploadAPI({ userSignDocId, expiryDate, isApproved: true });
            setApproveModalOpen(false);
            setSelectedUserSignDoc(null);
            gridContext.triggerTableRefresh();
        } catch (error) {
            console.error('Error approving document:', error);
            throw error; // Re-throw so modal can handle it
        }
    }, [gridContext]);

    const handleApproveClick = useCallback(async (row) => {
        // Check if SignDoc has expirationDuration
        const expirationDuration = get(row, 'signDoc.additionalData.expiryDuration', '');

        // If has expirationDuration, open modal to set expiry date
        if (expirationDuration && !isEmpty(expirationDuration)) {
            setSelectedUserSignDoc(row);
            setApproveModalOpen(true);
        } else {
            // If no expirationDuration, approve directly without modal
            await handleApprove(row.id, null);
        }
    }, [handleApprove]);

    const handleRejectClick = useCallback((row) => {
        setSelectedUserSignDoc(row);
        setRejectModalOpen(true);
    }, []);

    const handleReject = useCallback(async (userSignDocId, rejectionReason, deleteFiles) => {
        try {
            await approveOrRejectUserUploadAPI({
                userSignDocId,
                expiryDate: null,
                isApproved: false,
                rejectionReason
            });
            setRejectModalOpen(false);
            setSelectedUserSignDoc(null);
            gridContext.triggerTableRefresh();
        } catch (error) {
            console.error('Error rejecting document:', error);
            throw error; // Re-throw so modal can handle it
        }
    }, [gridContext]);

    const handleViewFile = useCallback((file) => {
        if (isPreviewableFile(file.mimeType) && file.signedUrl) {
            setPreviewFile(file);
        } else {
            // For non-previewable files, open in new tab
            window.open(file.signedUrl, '_blank');
        }
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewFile(null);
    }, []);

    const getColumnsCallback = useCallback(async () => {
        return getColumns({ handleApprove: handleApproveClick, handleRejectClick, handleViewFile });
    }, [handleApproveClick, handleRejectClick, handleViewFile]);

    const apiToGetData = useCallback(async ({ paginationModel, filterModel, sortModel }) => {
        const response = await getPendingApprovalsAPI({ paginationModel, filterModel, sortModel, types: [SignDocTypes.UPLOAD_DOCUMENT] });
        // Transform response to match DataGridWrapper expected format {data, count}
        return {
            data: response.userSignDocs,
            count: response.count
        };
    }, []);

    return (
        <>
            <DataGridWrapper
                getColumns={getColumnsCallback}
                apiToGetData={apiToGetData}
                dataConverter={convertData}
                filterModel={localTableFilters}
            />

            {/* Approve Upload Modal */}
            <ApproveUploadModal
                open={approveModalOpen}
                onClose={() => setApproveModalOpen(false)}
                userSignDoc={selectedUserSignDoc}
                onApprove={handleApprove}
            />

            {/* Reject Upload Modal */}
            <RejectUploadModal
                open={rejectModalOpen}
                onClose={() => setRejectModalOpen(false)}
                userSignDoc={selectedUserSignDoc}
                onReject={handleReject}
            />

            {/* File Preview */}
            <FilePreview
                file={previewFile}
                open={!!previewFile}
                onClose={handleClosePreview}
            />
        </>
    );
};

export default PendingApprovalsTable;
